import { useEffect, useState } from 'react';
import type { UnseenAchievement } from '../../services/achievements.service';
import { playCelebrationSound } from './celebrationSound';
import { getEventIcon, getTierTheme } from './achievementTheme';

interface Props {
  open: boolean;
  items: UnseenAchievement[];
  onClose: () => void;
  onComplete: (acknowledgedIds: string[]) => void;
}

export function AchievementsNotificationModal({ open, items, onClose, onComplete }: Props) {
  const [index, setIndex] = useState(0);
  const [confetti, setConfetti] = useState(0);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setConfetti((c) => c + 1);
      playCelebrationSound();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (index > 0) {
      playCelebrationSound();
      setConfetti((c) => c + 1);
    }
  }, [index, open]);

  if (!open || items.length === 0) return null;

  const current = items[index];
  const isLast = index === items.length - 1;
  const tierTheme = getTierTheme(current.achievement.tier);
  const eventIcon = getEventIcon(current.achievement.rule.event);

  function next() {
    if (isLast) {
      onComplete(items.map((i) => i.assignmentId));
      onClose();
    } else {
      setIndex((i) => i + 1);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        key={current.assignmentId}
        className="relative w-full max-w-md rounded-2xl bg-surface-container-low border border-primary/30 shadow-2xl overflow-hidden achievement-pop"
      >
        {/* Confetti burst */}
        <ConfettiBurst seed={confetti} />

        <div className="relative px-6 pt-10 pb-6 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-2">
            ¡Nuevo logro!
          </div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant mb-4">
            {tierTheme.emoji} Tier {current.achievement.tier} — {tierTheme.label}
          </div>

          <div className={`achievement-trophy mx-auto mb-2 w-24 h-24 rounded-full bg-gradient-to-br ${tierTheme.medalBg} flex items-center justify-center shadow-2xl ${tierTheme.glow}`}>
            <span className="material-symbols-outlined text-white text-[52px]">{eventIcon}</span>
          </div>
          <div className="text-3xl mb-3">{tierTheme.emoji}</div>

          <h2 className="text-2xl font-bold text-on-surface mb-2 achievement-title">
            {current.achievement.title}
          </h2>
          <p className="text-on-surface-variant mb-6 achievement-desc">
            {current.achievement.description}
          </p>

          <div className="flex items-center justify-center gap-1 mb-5">
            {items.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-6 bg-primary' : 'w-1.5 bg-outline-variant/40'
                }`}
              />
            ))}
          </div>

          <button
            onClick={next}
            className="w-full py-3 rounded-xl bg-primary text-on-primary font-semibold hover:opacity-90 transition-opacity"
          >
            {isLast ? '¡Genial!' : `Siguiente (${items.length - index - 1} más)`}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes achievement-pop-in {
          0% { transform: scale(0.85) translateY(20px); opacity: 0; }
          60% { transform: scale(1.03) translateY(-4px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        .achievement-pop {
          animation: achievement-pop-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }
        @keyframes trophy-bounce {
          0%, 100% { transform: scale(1) rotate(0); }
          25% { transform: scale(1.1) rotate(-8deg); }
          50% { transform: scale(1.15) rotate(0); }
          75% { transform: scale(1.1) rotate(8deg); }
        }
        .achievement-trophy {
          animation: trophy-bounce 1.4s ease-in-out infinite;
        }
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .achievement-title { animation: fade-up 0.4s ease 0.15s both; }
        .achievement-desc { animation: fade-up 0.4s ease 0.25s both; }
        .confetti-piece {
          position: absolute;
          top: -10px;
          width: 8px;
          height: 14px;
          opacity: 0.9;
          transform: rotate(0deg);
          animation: confetti-fall linear forwards;
        }
        @keyframes confetti-fall {
          0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(420px) rotate(540deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function ConfettiBurst({ seed }: { seed: number }) {
  const pieces = Array.from({ length: 18 }, (_, i) => i);
  return (
    <div key={seed} className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((i) => {
        const left = (i * 37) % 100;
        const delay = (i % 6) * 0.06;
        const hue = (i * 47) % 360;
        const duration = 1.1 + (i % 4) * 0.15;
        return (
          <span
            key={i}
            className="confetti-piece"
            style={{
              left: left + '%',
              animationDelay: delay + 's',
              animationDuration: duration + 's',
              backgroundColor: 'hsl(' + hue + ', 85%, 60%)',
            }}
          />
        );
      })}
    </div>
  );
}
